<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <ion-page>
    <ion-header
      v-if="showHeader"
      id="connected-header"
    >
      <!-- Archived / trashed workspace banner in small display -->
      <ion-text
        v-if="isSmallDisplay && workspaceIsArchivedOrTrashed"
        class="header-archived button-large"
      >
        <ion-icon
          :icon="currentWorkspace?.isArchived ? archive : trash"
          class="header-archived__icon"
        />
        {{
          currentWorkspace?.isArchived
            ? $msTranslate('WorkspacesPage.archiveWorkspace.isArchived')
            : $msTranslate('WorkspacesPage.archiveWorkspace.isTrashed')
        }}
      </ion-text>

      <ion-toolbar
        class="topbar"
        :class="currentRouteIs(Routes.History) ? 'topbar-history' : ''"
      >
        <!-- Sidebar toggle icon -->
        <ms-image
          v-if="isLargeDisplay"
          slot="start"
          id="trigger-toggle-menu-button"
          class="topbar-button__item"
          @click="isSidebarMenuVisible ? hideSidebarMenu() : resetSidebarMenu()"
          :image="SidebarToggle"
        />

        <div
          class="topbar-left"
          ref="topbarLeft"
        >
          <!-- Back button -->
          <div
            class="topbar-left-content"
            ref="backBlock"
            v-if="hasHistory() && !currentRouteIsWorkspaceManagementRoute()"
          >
            <header-back-button
              :short="currentRouteIsFileRoute()"
              class="topbar-left__back-button"
            />
          </div>

          <!-- Route title -->
          <div
            v-if="showRouteTitle"
            class="topbar-left-text"
          >
            <ion-label
              class="topbar-left-text__title title-h2"
              :class="hasHistory() || isLargeDisplay ? 'align-center' : 'align-left'"
            >
              {{ $msTranslate(routeTitle) }}
            </ion-label>
          </div>

          <!-- Breadcrumbs -->
          <div
            class="topbar-left__breadcrumb"
            v-if="currentRouteIs(Routes.Documents) || (currentRouteIs(Routes.Workspaces) && isLargeDisplay)"
          >
            <header-breadcrumbs
              :workspace-name="currentWorkspace?.name ?? ''"
              :path-nodes="fullPath"
              @change="onNodeSelected"
              :from-header-page="true"
              :available-width="breadcrumbsWidth"
            />
          </div>

          <!-- Workspace small display switch modal button (all/archived/bin) -->
          <div
            v-if="isSmallDisplay && currentRouteIsWorkspaceManagementRoute() && userInfo"
            class="topbar-left-workspaces-mobile"
          >
            <ion-text class="topbar-left-workspaces-mobile__orga body">{{ userInfo.organizationId }}</ion-text>
            <div
              class="topbar-left-workspaces-mobile-dropdown"
              @click="openWorkspacesSwitchModal"
            >
              <ion-icon
                v-if="currentRouteIsOneOf([Routes.Archived, Routes.Trash])"
                :icon="currentRouteIs(Routes.Archived) ? archive : trash"
                class="contextual-icon"
              />
              <ion-text class="topbar-left-workspaces-mobile-dropdown__title title-h2">
                {{ $msTranslate(workspaceSwitchModalTitle) }}
              </ion-text>
              <ion-icon
                :icon="chevronDown"
                class="topbar-left-workspaces-mobile-dropdown__icon"
              />
            </div>
          </div>
        </div>

        <!-- Top-right buttons -->
        <ion-buttons
          slot="primary"
          class="topbar-right"
        >
          <div
            class="topbar-right-buttons"
            v-if="!currentRouteIsOneOf([Routes.History, Routes.MyProfile, Routes.Invitations])"
          >
            <!-- Invitations button -->
            <invitations-button v-if="showInvitationsButton" />

            <!-- Small display options button -->
            <ion-button
              v-if="isSmallDisplay && !currentRouteIsWorkspaceManagementRoute()"
              slot="icon-only"
              class="topbar-right-buttons__item"
              id="trigger-contextual-menu-button"
              @click="openContextualMenu($event)"
              ref="contextualMenuButton"
            >
              <ion-icon :icon="ellipsisHorizontal" />
            </ion-button>

            <!-- Notifications button -->
            <ion-button
              slot="icon-only"
              id="trigger-notifications-button"
              class="topbar-right-buttons__item"
              :class="{
                active: notificationPopoverIsVisible,
                unread: informationManager.notificationManager.hasUnreadNotifications(),
              }"
              @click="openNotificationCenter($event)"
              ref="notificationCenterButton"
            >
              <ion-icon :icon="notifications" />
            </ion-button>
          </div>

          <!-- Profile button -->
          <profile-header-organization
            v-if="isLargeDisplay"
            id="profile-button"
            :name="userInfo ? userInfo.humanHandle.label : ''"
            :email="userInfo ? userInfo.humanHandle.email : ''"
            :profile="userInfo ? userInfo.currentProfile : UserProfile.Outsider"
            class="profile-header"
          />
        </ion-buttons>
      </ion-toolbar>

      <small-display-security-recommendations
        v-if="showSmallDisplaySecurityRecommendations"
        @click="openSecurityWarningsModal()"
        :user-profile="userInfo ? userInfo.currentProfile : UserProfile.Outsider"
        :security-warnings="securityWarnings!"
      />
    </ion-header>

    <ion-content>
      <ion-router-outlet />
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { pxToRem } from '@/common/utils';
import HeaderBackButton from '@/components/header/HeaderBackButton.vue';
import HeaderBreadcrumbs, { RouterPathNode } from '@/components/header/HeaderBreadcrumbs.vue';
import InvitationsButton from '@/components/header/InvitationsButton.vue';
import SmallDisplaySecurityRecommendations from '@/components/header/SmallDisplaySecurityRecommendations.vue';
import { RecommendationAction, SecurityWarnings, getSecurityWarnings } from '@/components/misc';
import RecommendationChecklistPopoverModal from '@/components/misc/RecommendationChecklistPopoverModal.vue';
import { ClientInfo, Path, UserProfile, WorkspaceInfo, WorkspaceRole, getClientInfo, getWorkspaceInfo } from '@/parsec';
import {
  ProfilePages,
  Routes,
  currentRouteIs,
  currentRouteIsFileRoute,
  currentRouteIsLoggedRoute,
  currentRouteIsOneOf,
  currentRouteIsUserRoute,
  currentRouteIsWorkspaceManagementRoute,
  getCurrentRouteName,
  getCurrentRouteParams,
  getDocumentPath,
  getWorkspaceHandle,
  hasHistory,
  navigateTo,
  routerGoBack,
  watchRoute,
} from '@/router';
import {
  EventData,
  EventDistributor,
  EventDistributorKey,
  Events,
  OpenContextualMenuData,
  WorkspaceRoleUpdateData,
} from '@/services/eventDistributor';
import useHeaderControl from '@/services/headerControl';
import { HotkeyGroup, HotkeyManager, HotkeyManagerKey, Modifiers, Platforms } from '@/services/hotkeyManager';
import { InformationManager, InformationManagerKey } from '@/services/informationManager';
import useSidebarMenu from '@/services/sidebarMenu';
import NotificationCenterModal from '@/views/header/NotificationCenterModal.vue';
import NotificationCenterPopover from '@/views/header/NotificationCenterPopover.vue';
import ProfileHeaderOrganization from '@/views/header/ProfileHeaderOrganization.vue';
import WorkspaceSwitchModal from '@/views/header/WorkspaceSwitchModal.vue';
import { openSettingsModal } from '@/views/settings';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPage,
  IonRouterOutlet,
  IonText,
  IonToolbar,
  modalController,
  popoverController,
} from '@ionic/vue';
import { archive, chevronDown, ellipsisHorizontal, home, notifications, trash } from 'ionicons/icons';
import { MsImage, MsModalResult, SidebarToggle, Translatable, useWindowSize } from 'megashark-lib';
import { Ref, computed, inject, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

const { windowWidth, isLargeDisplay, isSmallDisplay } = useWindowSize();
const hotkeyManager: HotkeyManager = inject(HotkeyManagerKey)!;
let hotkeys: HotkeyGroup | null = null;
let eventDistributorCbId: string | null = null;
const { isVisible: isSidebarMenuVisible, reset: resetSidebarMenu, hide: hideSidebarMenu } = useSidebarMenu();
const { isHeaderVisible } = useHeaderControl();
const userInfo: Ref<ClientInfo | null> = ref(null);
const fullPath: Ref<RouterPathNode[]> = ref([]);
const notificationPopoverIsVisible: Ref<boolean> = ref(false);
const informationManager: Ref<InformationManager> = inject(InformationManagerKey)!;
const eventDistributor: Ref<EventDistributor> = inject(EventDistributorKey)!;
const notificationCenterButtonRef = useTemplateRef('notificationCenterButton');
const securityWarnings = ref<SecurityWarnings | undefined>();

const workspaceIsArchivedOrTrashed = computed(
  () => currentWorkspace.value && (currentWorkspace.value.isArchived || currentWorkspace.value.isTrashed),
);

const showHeader = computed(() => {
  // Override visibility for specific routes
  if (currentRouteIs(Routes.FileHandler) || currentRouteIsUserRoute() || currentRouteIsFileRoute()) {
    return isHeaderVisible();
  }
  // Hide header on small displays for specific routes
  if (isSmallDisplay.value && (currentRouteIs(Routes.Organization) || currentRouteIs(Routes.MyProfile))) {
    return false;
  }
  return true;
});
const breadcrumbsWidth = ref(0);
const backBlockRef = useTemplateRef('backBlock');
const topbarLeftRef = useTemplateRef('topbarLeft');
const showRouteTitle = computed(
  () => !currentRouteIsFileRoute() && !(isSmallDisplay.value && currentRouteIsOneOf([Routes.Archived, Routes.Trash])),
);
const showInvitationsButton = computed(() => currentRouteIsOneOf([Routes.Workspaces, Routes.Archived, Routes.Trash, Routes.Users]));
const showSmallDisplaySecurityRecommendations = computed(() => {
  return (
    !currentRouteIsOneOf([Routes.Invitations, Routes.History, Routes.Archived, Routes.Trash]) &&
    isSmallDisplay.value &&
    securityWarnings.value &&
    (!securityWarnings.value.hasMultipleDevices ||
      !securityWarnings.value.hasRecoveryDevice ||
      securityWarnings.value.soloOwnerWorkspaces.length > 0) &&
    !currentWorkspace.value?.isArchived &&
    !currentWorkspace.value?.isTrashed
  );
});
const workspaceSwitchModalTitle = computed(() => {
  if (currentRouteIs(Routes.Archived)) {
    return 'HeaderPage.titles.archived';
  } else if (currentRouteIs(Routes.Trash)) {
    return 'HeaderPage.titles.trashed';
  }
  return 'HeaderPage.titles.workspaces';
});

const currentWorkspace: Ref<WorkspaceInfo | undefined> = ref(undefined);

const routeTitle = computed((): Translatable => {
  switch (getCurrentRouteName()) {
    case Routes.Users:
      return 'HeaderPage.titles.users';
    case Routes.Invitations:
      return 'HeaderPage.titles.invitationsRequests';
    case Routes.Storage:
      return 'HeaderPage.titles.organization.storage';
    case Routes.Organization:
      return 'HeaderPage.titles.organization.information';
    case Routes.MyProfile:
      return 'HeaderPage.titles.myProfile';
    case Routes.History:
      return 'HeaderPage.titles.history';
    case Routes.Archived:
      return 'HeaderPage.titles.archived';
    case Routes.Trash:
      return 'HeaderPage.titles.trashed';
    case null:
      return '';
  }
  return '';
});

const topbarWidthWatchCancel = watch([windowWidth, fullPath], () => {
  if (topbarLeftRef.value?.offsetWidth && backBlockRef.value?.offsetWidth) {
    breadcrumbsWidth.value = pxToRem(topbarLeftRef.value.offsetWidth - backBlockRef.value.offsetWidth);
  }
});

const routeWatchCancel = watchRoute(async () => {
  if (!currentRouteIsLoggedRoute()) {
    return;
  }
  const result = await getClientInfo();
  if (result.ok) {
    userInfo.value = result.value;
  } else {
    window.nativeAPI.log('error', `Failed to retrieve user info ${JSON.stringify(result.error)}`);
  }
  await updateRoute();
});

async function onNodeSelected(node: RouterPathNode): Promise<void> {
  await navigateTo(node.route, { params: node.params, query: node.query });
}

async function updateRoute(): Promise<void> {
  currentWorkspace.value = undefined;

  if (!currentRouteIsFileRoute()) {
    fullPath.value = [];
    return;
  }

  if (currentRouteIs(Routes.Workspaces)) {
    fullPath.value = [
      {
        id: 0,
        title: 'HeaderPage.titles.workspaces',
        icon: home,
        route: Routes.Workspaces,
        params: {},
      },
    ];
  } else if (currentRouteIs(Routes.Documents)) {
    const workspaceHandle = getWorkspaceHandle();
    if (workspaceHandle) {
      const wInfo = await getWorkspaceInfo(workspaceHandle);
      if (wInfo.ok) {
        currentWorkspace.value = wInfo.value;
      }
    }

    const finalPath: RouterPathNode[] = [];

    let firstItem = { icon: home, title: '', route: Routes.Workspaces };
    if (currentWorkspace.value?.isArchived) {
      firstItem = { icon: archive, title: 'HeaderPage.breadcrumbs.archived', route: Routes.Archived };
    } else if (currentWorkspace.value?.isTrashed) {
      firstItem = { icon: trash, title: 'HeaderPage.breadcrumbs.trashed', route: Routes.Trash };
    }

    finalPath.push({
      id: 0,
      ...firstItem,
      params: {},
    });

    const rebuildPath: string[] = [];
    const workspacePath = await Path.parse(getDocumentPath());
    finalPath.push({
      id: 1,
      display: currentWorkspace.value?.name ?? '',
      route: Routes.Documents,
      popoverIcon: home,
      query: { documentPath: '/', workspaceHandle: workspaceHandle },
      params: getCurrentRouteParams(),
    });
    for (let i = 0; i < workspacePath.length; i++) {
      rebuildPath.push(workspacePath[i]);
      finalPath.push({
        id: i + 2,
        display: workspacePath[i],
        route: Routes.Documents,
        query: { documentPath: `/${rebuildPath.join('/')}`, workspaceHandle: workspaceHandle },
        params: getCurrentRouteParams(),
      });
    }

    fullPath.value = finalPath;
  }
}

onMounted(async () => {
  hotkeys = hotkeyManager.newHotkeys();
  hotkeys.add({ key: ',', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop, disableIfModal: true }, openSettingsModal);
  hotkeys.add(
    { key: 'arrowup', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop, disableIfModal: true },
    async () => await routerGoBack(),
  );
  hotkeys.add(
    { key: 'arrowleft', modifiers: Modifiers.Ctrl, platforms: Platforms.Desktop, disableIfModal: true },
    async () => await routerGoBack(),
  );
  hotkeys.add(
    { key: 'n', modifiers: Modifiers.Ctrl | Modifiers.Shift, platforms: Platforms.Desktop, disableIfModal: true },
    async () => await notificationCenterButtonRef.value?.$el.click(),
  );

  const result = await getClientInfo();
  if (result.ok) {
    userInfo.value = result.value;
  } else {
    window.nativeAPI.log('error', `Failed to retrieve user info ${JSON.stringify(result.error)}`);
  }
  await updateRoute();

  eventDistributorCbId = await eventDistributor.value.registerCallback(
    [Events.WorkspaceCreated, Events.MenuAction, Events.WorkspaceRoleUpdate, Events.DeviceCreated, Events.WorkspaceUpdated],
    async (event: Events, data?: EventData) => {
      if (event === Events.WorkspaceCreated) {
        securityWarnings.value = await getSecurityWarnings();
      } else if (event === Events.DeviceCreated) {
        if (!securityWarnings.value || !securityWarnings.value.hasMultipleDevices || !securityWarnings.value.hasRecoveryDevice) {
          securityWarnings.value = await getSecurityWarnings();
        }
      } else if (event === Events.WorkspaceRoleUpdate) {
        const updateData = data as WorkspaceRoleUpdateData;

        if (updateData.newRole === null || updateData.newRole === WorkspaceRole.Owner) {
          securityWarnings.value = await getSecurityWarnings();
        }
      } else if (event === Events.Online) {
        securityWarnings.value = await getSecurityWarnings();
      } else if (event === Events.WorkspaceUpdated) {
        securityWarnings.value = await getSecurityWarnings();
        await updateRoute();
      }
    },
  );
  securityWarnings.value = await getSecurityWarnings();
});

onUnmounted(async () => {
  if (hotkeys) {
    hotkeyManager.unregister(hotkeys);
  }
  if (eventDistributorCbId) {
    eventDistributor.value.removeCallback(eventDistributorCbId);
  }
  routeWatchCancel();
  topbarWidthWatchCancel();
});

async function openContextualMenu(event: Event): Promise<void> {
  event.stopPropagation();

  if (currentRouteIs(Routes.Documents) || currentRouteIs(Routes.Users)) {
    await eventDistributor.value.dispatchEvent(Events.OpenContextMenu, {
      event,
    } as OpenContextualMenuData);
  }
}

async function openNotificationCenter(event: Event): Promise<void> {
  event.stopPropagation();
  notificationPopoverIsVisible.value = true;

  if (isLargeDisplay.value) {
    const popover = await popoverController.create({
      component: NotificationCenterPopover,
      alignment: 'center',
      event: event,
      cssClass: 'notification-center-popover',
      showBackdrop: false,
      componentProps: {
        notificationManager: informationManager.value.notificationManager,
        eventDistributor: eventDistributor.value,
      },
    });
    await popover.present();
    await popover.onDidDismiss();
    await popover.dismiss();
  } else {
    const modal = await modalController.create({
      component: NotificationCenterModal,
      cssClass: 'notification-center-modal',
      showBackdrop: true,
      handle: true,
      backdropDismiss: true,
      breakpoints: isLargeDisplay.value ? undefined : [0, 0.5, 1],
      expandToScroll: false,
      initialBreakpoint: isLargeDisplay.value ? undefined : 0.5,
      componentProps: {
        notificationManager: informationManager.value.notificationManager,
        eventDistributor: eventDistributor.value,
      },
    });
    await modal.present();
    await modal.onDidDismiss();
    await modal.dismiss();
  }
  notificationPopoverIsVisible.value = false;
}

async function openSecurityWarningsModal(): Promise<void> {
  if (!securityWarnings.value) {
    return;
  }
  const modal = await modalController.create({
    component: RecommendationChecklistPopoverModal,
    cssClass: 'small-display-recommendation-checklist',
    showBackdrop: true,
    handle: true,
    backdropDismiss: true,
    expandToScroll: false,
    initialBreakpoint: isLargeDisplay.value ? undefined : 1,
    componentProps: {
      securityWarnings: securityWarnings.value,
      isModal: true,
    },
  });
  await modal.present();
  const { data, role } = await modal.onWillDismiss();
  await modal.dismiss();
  if (role === MsModalResult.Confirm && data.action) {
    if (data.action === RecommendationAction.AddDevice) {
      await navigateTo(Routes.MyProfile, { query: { profilePage: ProfilePages.Devices } });
    } else if (data.action === RecommendationAction.CreateRecoveryFiles) {
      await navigateTo(Routes.MyProfile, { query: { profilePage: ProfilePages.Recovery } });
    } else if (data.action === RecommendationAction.AddWorkspaceOwner) {
      await navigateTo(Routes.Workspaces);
    }
  }
}

async function openWorkspacesSwitchModal(): Promise<void> {
  const currentRoute = getCurrentRouteName();
  const modal = await modalController.create({
    component: WorkspaceSwitchModal,
    canDismiss: true,
    backdropDismiss: true,
    cssClass: 'workspace-switch-modal',
    showBackdrop: true,
    breakpoints: [1],
    expandToScroll: false,
    initialBreakpoint: isLargeDisplay.value ? undefined : 1,
    componentProps: {
      currentRoute: currentRoute && [Routes.Workspaces, Routes.Archived, Routes.Trash].includes(currentRoute) ? currentRoute : undefined,
    },
  });

  await modal.present();
  const { data, role } = await modal.onWillDismiss();
  await modal.dismiss();

  if (role !== MsModalResult.Confirm || currentRouteIs(data)) {
    return;
  }

  await navigateTo(data);
}
</script>

<style scoped lang="scss">
// remove toolbar border added by ionic on iOS
#connected-header {
  ion-toolbar:last-of-type {
    --border-width: 0;
  }
}

.header-archived {
  background: var(--parsec-color-light-secondary-text);
  color: var(--parsec-color-light-secondary-premiere);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;

  &__icon {
    color: var(--parsec-color-light-secondary-premiere);
    width: 1rem;
  }
}

.topbar {
  --background: var(--parsec-color-light-secondary-white);
  display: flex;
  padding: 1rem 1.5rem;

  &::part(container) {
    @include ms.responsive-breakpoint('sm') {
      contain: none;
      overflow: visible;
      gap: 0.5rem;
    }
  }

  &::part(content) {
    overflow: hidden;
  }

  @include ms.responsive-breakpoint('sm') {
    padding: 1.5rem 1.5rem 1rem;
    --background: var(--parsec-color-light-secondary-background);
  }

  #trigger-toggle-menu-button {
    --fill-color: var(--parsec-color-light-secondary-grey);
    padding: 0.625rem;
    border-radius: var(--parsec-radius-12);
    cursor: pointer;

    &:hover {
      background: var(--parsec-color-light-secondary-premiere);
      --fill-color: var(--parsec-color-light-secondary-hard-grey);
    }
  }
}

.topbar-left {
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
  overflow: hidden;

  @include ms.responsive-breakpoint('sm') {
    margin-right: 0 !important;
    gap: 0.5rem;
  }

  &-workspaces-mobile {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    overflow: hidden;

    &__orga {
      color: var(--parsec-color-light-secondary-grey);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__title {
      color: var(--parsec-color-light-primary-800);
    }

    &-dropdown {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      cursor: pointer;

      .contextual-icon {
        font-size: 1.25rem;
        margin-right: 0.125rem;
      }

      &__icon {
        color: var(--parsec-color-light-secondary-grey);
      }
    }
  }

  &-text {
    width: 100%;
    color: var(--parsec-color-light-primary-800);
    text-align: center;

    .align-left {
      display: flex;
      justify-content: start;
      align-items: center;
      margin-inline: 1.5rem;
    }

    .align-center {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }

  &__breadcrumb {
    display: flex;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.topbar-right {
  display: flex;
  gap: 1.5em;
  margin-inline-end: 0;

  &-buttons {
    display: flex;
    gap: 0.75em;
    align-items: center;

    &::part(native) {
      --background: transparent;
      --background-activated: transparent;
      --background-focused: transparent;
    }

    &::after {
      content: '';
      display: block;
      width: 1px;
      height: 1.5em;
      background: var(--parsec-color-light-secondary-light);

      @include ms.responsive-breakpoint('sm') {
        display: none;
      }
    }

    // eslint-disable-next-line vue-scoped-css/no-unused-selector
    &__item {
      padding: 0.625rem;
      border-radius: var(--parsec-radius-12);
      cursor: pointer;

      &::part(native) {
        --padding-top: 0;
        --padding-start: 0;
        --padding-end: 0;
        --padding-bottom: 0;
        min-height: 0;
      }

      ion-icon {
        color: var(--parsec-color-light-secondary-grey);
        font-size: 1.375rem;
      }

      &:hover {
        --background-hover: var(--parsec-color-light-secondary-medium);
        background: var(--parsec-color-light-secondary-medium);
        color: var(--parsec-color-light-secondary-text);
      }

      &.active {
        --background-hover: var(--parsec-color-light-primary-50);
        background: var(--parsec-color-light-primary-50);

        ion-icon {
          color: var(--parsec-color-light-primary-700);
        }
      }

      &#trigger-notifications-button.unread {
        position: relative;

        &::after {
          content: '';
          position: absolute;
          background: var(--parsec-color-light-danger-500);
          right: 0.45rem;
          top: 0.35rem;
          width: 0.625rem;
          height: 0.625rem;
          border: 2px solid var(--parsec-color-light-secondary-inversed-contrast);
          border-radius: var(--parsec-radius-12);
        }
      }

      @include ms.responsive-breakpoint('sm') {
        padding: 0.5rem;
        background: var(--parsec-color-light-secondary-white);
        border-radius: var(--parsec-radius-circle);
        box-shadow: var(--parsec-shadow-soft);

        ion-icon {
          color: var(--parsec-color-light-secondary-hard-grey);
          font-size: 1.375rem;
        }
      }
    }
  }
}

.topbar-history {
  --background: var(--parsec-color-light-secondary-background);

  @include ms.responsive-breakpoint('sm') {
    .topbar-left {
      justify-content: center;
      width: 100%;
      margin: 0;

      &-content {
        position: absolute;
        top: 0;
        left: 0;
      }

      &-text {
        display: flex;
        flex-direction: column;
        align-items: center;

        &__title {
          color: var(--parsec-color-light-primary-800);
        }

        &__workspace {
          color: var(--parsec-color-light-secondary-grey);
        }
      }
    }
  }
}
</style>
